import { Icon } from '@iconify/react';
import classNames from 'classnames';
import type { FC } from 'react';

import Chip from '@/components/Chip';
import { LANGUAGES_LEVEL_OPTIONS } from '@/components/Dashboard/Filters';
import SeniorityChip from '@/components/Dashboard/SeniorityChip';
import Text from '@/components/Text';
import { useLanguages } from '@/hooks/useLanguages';
import type { TransformApiCandidateResponse } from '@/services/externalCandidates';
import { useDashboardFiltersStore } from '@/stores/useDashboardFiltersStore';
import { capitalizeFirstLetter } from '@/utils/cvGenerator';

import Section from './Section';

type Props = {
  data: TransformApiCandidateResponse;
};
const PersonalData: FC<Props> = ({ data }) => {
  const { data: languagesData } = useLanguages();
  const { filters } = useDashboardFiltersStore();

  const {
    candidateDetailedInfo,
    cvFile,
    languages,
    paymentPeriod,
    salary,
    currency,
  } = data;

  const languageProficiencies = languages.map(([language, level]) => {
    const languageName = languagesData?.results.find(
      (languageData) => languageData.id === language
    )?.name;

    const languageLevel = LANGUAGES_LEVEL_OPTIONS.find(
      (option) => option.value === level
    )?.label;

    return {
      language: languageName,
      level: languageLevel,
    };
  });

  const candidateInfo = [
    {
      value: (candidateDetailedInfo?.availability?.split('_') ?? [])
        .map((e) => capitalizeFirstLetter(e))
        .join(' '),
      label: 'Availability',
      icon: 'material-symbols:calendar-month-outline',
    },
    {
      value: (
        <SeniorityChip seniority={candidateDetailedInfo?.seniorityLevel} />
      ),
      label: 'Seniority',
      icon: 'si:briefcase-line',
    },
    {
      value: `${candidateDetailedInfo?.commercialExperience} years`,
      label: 'Experience',
      icon: 'material-symbols:timer-outline-rounded',
    },
    {
      value: candidateDetailedInfo?.otherLocation,
      label: 'Location',
      icon: 'material-symbols:pin-drop-outline',
    },
    {
      value: candidateDetailedInfo?.offer?.title,
      label: 'Offer',
      icon: 'material-symbols:play-for-work',
    },
    {
      value:
        salary && currency
          ? `${salary} ${currency} ${paymentPeriod ? `/${paymentPeriod}` : ''}`
          : undefined,
      label: 'Salary',
      icon: 'majesticons:coins-line',
    },
    {
      value: candidateDetailedInfo?.typeOfContract,
      label: 'Contract',
      icon: 'material-symbols:description',
    },
  ].filter(({ value }) => value);

  return (
    <>
      <div className="flex flex-row flex-wrap gap-10">
        {candidateInfo.map(({ icon, label, value }) => (
          <div
            className="flex max-w-[200px] flex-row gap-1"
            key={`${value?.toString()}-${label}`}
          >
            <Icon icon={icon} className="mt-0.5 text-primary-500" />
            <div className="flex flex-col gap-0">
              <Text variant="body-small" className="font-semibold">
                {label}
              </Text>
              {label !== 'Seniority' && (
                <Text variant="body-small" className="text-neutral-700">
                  {value}
                </Text>
              )}
              {label === 'Seniority' && value}
            </div>
          </div>
        ))}
      </div>
      <Section label="Languages" className="flex flex-row gap-6">
        {languageProficiencies.map(({ language, level }) => (
          <Chip
            size="sm"
            variant="primary-light"
            key={language}
            className="flex flex-row gap-2 !py-0"
          >
            <Text variant="body-small" className="text-primary-600">
              {[language, level].join(' - ')}{' '}
            </Text>
          </Chip>
        ))}
      </Section>
      <Section
        label="Technologies & Skills"
        className="flex flex-row flex-wrap gap-3"
      >
        {candidateDetailedInfo?.skills?.map((skill) => (
          <Chip
            className="w-fit"
            size="sm-long"
            variant={
              filters.skills.includes(skill) ? 'primary' : 'primary-light'
            }
            key={skill}
          >
            <Text
              variant="body-small"
              className={classNames('w-fit ', {
                'text-neutral-1000': !filters.skills.includes(skill),
              })}
            >
              {skill}
            </Text>
          </Chip>
        ))}
      </Section>
      {!!candidateDetailedInfo?.wantToUseSkills?.length && (
        <Section label="Wants to use" className="flex flex-row flex-wrap gap-3">
          {candidateDetailedInfo?.wantToUseSkills.map((wantToUseSkill) => (
            <Chip
              className="w-fit"
              size="sm-long"
              variant="rounded-light-gray"
              key={wantToUseSkill}
            >
              <Text variant="body-small">{wantToUseSkill}</Text>
            </Chip>
          ))}
        </Section>
      )}
      <div className="flex min-h-[800px] flex-1 flex-col rounded-lg p-8">
        <iframe
          title="cv"
          className="w-full max-w-4xl flex-1 shrink-0 self-center overflow-hidden"
          src={`${cvFile}#view=FitH`}
        />
      </div>
      {data.candidateDetailedInfo?.videoFile && (
        <div className="flex flex-5 rounded-lg p-7">
          <video
            src={data.candidateDetailedInfo.videoFile}
            className="aspect-video w-full rounded-lg"
            controls
          >
            <track kind="captions" srcLang="en" label="English" default />
          </video>
        </div>
      )}
    </>
  );
};

export default PersonalData;
